import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Client } from '../types/client'
import { clientApi } from '../services/clientApi'

interface ClientState {
  clients: Client[]
  currentClientId: string | null
  loading: boolean
  error: string | null
  fetchClients: () => Promise<void>
  setCurrentClient: (id: string | null) => void
  createClient: (data: Partial<Client>) => Promise<Client>
  updateClient: (id: string, data: Partial<Client>) => Promise<Client>
  deleteClient: (id: string) => Promise<void>
  reset: () => void
}

function buildHierarchy(clients: Client[]): Client[] {
  const map = new Map<string, Client>()
  const roots: Client[] = []
  for (const c of clients) {
    map.set(c.id, { ...c, children: [] })
  }
  for (const c of map.values()) {
    if (c.parent_id && map.has(c.parent_id)) {
      map.get(c.parent_id)!.children!.push(c)
    } else {
      roots.push(c)
    }
  }
  return roots
}

export const useClientStore = create<ClientState>()(
  persist(
    (set, get) => ({
      clients: [],
      currentClientId: null,
      loading: false,
      error: null,

      fetchClients: async () => {
        set({ loading: true, error: null })
        try {
          const clients = await clientApi.getClients()
          console.log('[clientStore] clients loaded:', clients.length)
          set({ clients, loading: false })
        } catch (e) {
          console.error('[clientStore] fetchClients error:', e)
          set({ error: e instanceof Error ? e.message : 'Erreur chargement clients', loading: false })
        }
      },

      setCurrentClient: (id) => {
        console.log('[clientStore] setCurrentClient:', id)
        set({ currentClientId: id })
      },

      createClient: async (data) => {
        const client = await clientApi.createClient(data)
        await get().fetchClients()
        return client
      },

      updateClient: async (id, data) => {
        const client = await clientApi.updateClient(id, data)
        await get().fetchClients()
        return client
      },

      deleteClient: async (id) => {
        await clientApi.deleteClient(id)
        if (get().currentClientId === id) {
          set({ currentClientId: null })
        }
        await get().fetchClients()
      },

      reset: () => {
        set({ clients: [], currentClientId: null, loading: false, error: null })
      },
    }),
    {
      name: 'geocontexte-client',
      partialize: (state) => ({ currentClientId: state.currentClientId }),
    }
  )
)

// Derived selectors
export const selectCurrentClient = (state: ClientState) =>
  state.clients.find((c) => c.id === state.currentClientId) ?? null

export const selectClientHierarchy = (state: ClientState) => buildHierarchy(state.clients)
